"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Loader as Loader2, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-sm">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="font-default text-xl font-semibold">Inquiry Received</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">
          {successMessage || "Thank you. Our analytical application specialists will review your requirements and respond promptly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Please enter at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide more details (at least 10 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Your message..." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto px-8" size="lg">
        {isSubmitting ? <>
            <Loader2 className="size-4 animate-spin" />
            Submitting Inquiry...
          </> : <>
            <Send className="size-4" />
            Send Inquiry
          </>}
      </Button>
    </Form>;
}
export default function Inquiry() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="inquirycta" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="mx-auto max-w-3xl">
          <Card className="border border-border bg-secondary text-secondary-foreground shadow-md">
            <CardHeader className="text-center sm:text-left border-b border-border/60 pb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                Technical Consultation
              </span>
              <CardTitle className="font-default text-2xl md:text-3xl font-bold tracking-tight text-secondary-foreground">
                Contact Us - Antech Solutions
              </CardTitle>
              <CardDescription className="text-muted-foreground text-base">
                Discuss application feasibility, qualification services, or analytical instrument specifications with our technical engineering team.
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-6">
              <FormProvider formId="contact_form" sectionName="inquirycta" defaultValues={{
              name: "",
              work_email: "",
              company_name: "",
              phone_number: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              work_email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Send Inquiry" successMessage="Thank you for reaching out. An application specialist from Antech Solutions will follow up shortly." errorMessage="Unable to send your inquiry. Please verify the information and try again.">
                <InquiryFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}